import { Award, Globe2, Heart, Rocket, Target, Users, Building2, Sparkles, Code2, ShieldCheck } from "lucide-react";
import Reveal from "../components/Reveal";
import { COMPANY, FOUNDERS, KMC_BG, ABOUT_GALLERY } from "../lib/constants";
import { motion } from "framer-motion";

const VALUES = [
  { icon: Rocket, title: "Innovation First", desc: "We ship ideas that move the needle, not just code.", color: "from-[#FF6B1A] to-[#FFAA33]" },
  { icon: Heart, title: "Client Obsession", desc: "Long-term relationships built on outcomes, not invoices.", color: "from-[#3B82F6] to-[#60A5FA]" },
  { icon: Target, title: "Pragmatic Quality", desc: "Beautiful engineering that ships on time, every time.", color: "from-[#FFAA33] to-[#FF6B1A]" },
  { icon: Globe2, title: "Global Mindset", desc: "Local roots in Kolkata, clients across continents.", color: "from-[#60A5FA] to-[#3B82F6]" },
];

const TIMELINE = [
  { year: "2025 · Q1", title: "Founded in Kolkata", desc: "Codeflare Labs is born — a SaaS studio with a mission to bridge education and software. Akash Sarkar (CEO) and Arnab Bera (COO) start with a small but obsessive team of engineers in Kolkata." },
  { year: "2025 · Q1", title: "First client — Pather Khonje", desc: "Our very first client. We delivered a custom website + booking flow for Pather Khonje, a travel-experience brand. The proceeds funded our first office and our second hire." },
  { year: "2025 · Q2", title: "Second client — Gift Of Memories", desc: "Built a full ecommerce + storytelling platform for Gift Of Memories, a personalised gifting brand. Within 90 days they crossed their first 1,000 paid orders — our first taste of building things that scale." },
  { year: "2025 · Q2", title: "Going international — Australia", desc: "Onboarded our first overseas clients in Australia (multi-vendor retail) and Singapore (Web3 fintech). Codeflare officially became an international SaaS partner." },
  { year: "2025 · Q3", title: "First Govt project", desc: "Selected as a vendor for a major civic body in Kolkata — built citizen services dashboards and internal admin tools. Our first government engagement, opening doors to public-sector work across India." },
  { year: "2025 · Q3", title: "Education partnerships", desc: "Signed our first MoUs with engineering colleges and universities in West Bengal — including Brainware University — for training, internships and placement programs on our production stacks." },
  { year: "2025 · Q4", title: "10+ countries served", desc: "Active client engagements across India, Australia, Singapore, UAE, USA, UK and more — a true international company headquartered in Kolkata." },
  { year: "2026 · Q1", title: "50+ active clients · 20+ team", desc: "Crossed 50 clients across 11 service verticals with a 20+ strong delivery team. Launched our own SaaS products — Codeflare CRM, LMS and ERP — built from the same playbooks we use for client work." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-20 relative">
      <div className="absolute inset-0 -z-10">
        <div className="orb orb-flame w-[500px] h-[500px] -top-32 -left-32 opacity-30" />
        <div className="orb orb-azure w-[600px] h-[600px] top-1/3 -right-40 opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Hero */}
        <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">About us</span></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">
            We bridge <span className="fb-text">learning</span><br /> and real-world software.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-7 max-w-3xl text-xl text-zinc-300 leading-relaxed">
            Codeflare Labs is a Kolkata-born SaaS company founded in {COMPANY.founded}. We empower the future
            with technology and education — building production-grade software for 50+ clients across India,
            Australia and beyond, while training the next generation of engineers.
          </p>
        </Reveal>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
          {[
            { n: "50+", l: "Clients delivered", c: "from-[#FF6B1A] to-[#FFAA33]" },
            { n: "20+", l: "Team strength", c: "from-[#3B82F6] to-[#60A5FA]" },
            { n: "11", l: "Service verticals", c: "from-[#FFAA33] to-[#FF6B1A]" },
            { n: "10+", l: "Countries served", c: "from-[#60A5FA] to-[#3B82F6]" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 0.05}>
              <motion.div whileHover={{ y: -6 }} className="glass rounded-2xl p-6 text-center h-full">
                <div className={`text-2xl md:text-3xl font-bold bg-gradient-to-r ${s.c} bg-clip-text text-transparent`}>{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2">{s.l}</div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* Story + Software-development visual */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-28 items-center">
          <Reveal>
            <motion.div whileHover={{ scale: 1.02 }} className="rounded-3xl overflow-hidden border border-white/10 relative tilt-card group">
              <img src={KMC_BG} alt="Codeflare Labs engineers shipping software" className="w-full h-[460px] object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-7">
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-300">Software Studio</span>
                <p className="mt-3 text-2xl font-bold">Engineering, design & ops under one roof</p>
                <p className="text-sm text-zinc-300 mt-1">A 20+ strong team in Kolkata shipping production-grade software across web, mobile, AI, ecommerce and ERP — for 50+ clients in 10+ countries.</p>
              </div>
            </motion.div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="text-xs font-bold tracking-[0.25em] text-[#3B82F6] uppercase">Our story</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-bold">From a Kolkata studio<br /> to an <span className="azure-text">international SaaS</span>.</h2>
            <p className="text-zinc-300 leading-relaxed mt-6">
              Codeflare Labs was founded in Kolkata in {COMPANY.founded} by Akash Sarkar and Arnab Bera. Our very first client
              was <span className="text-white font-semibold">Pather Khonje</span> — a travel brand that trusted a 3-person studio
              with their digital presence. Soon after, we built the ecommerce + storytelling platform for our second client,
              <span className="text-white font-semibold"> Gift Of Memories</span>, who scaled from zero to a thousand paid orders
              in their first quarter with us.
            </p>
            <p className="text-zinc-400 leading-relaxed mt-4">
              From those two early bets we have grown into a full-service SaaS partner working with 50+ clients across
              <span className="text-white"> India, Australia, Singapore, UAE, the USA, the UK </span>and more — making
              Codeflare Labs a true <span className="fb-text font-semibold">international company</span> headquartered in Kolkata.
              We also serve government and civic bodies, and run education partnerships with leading universities like Brainware
              University to train the next generation of engineers on the same stacks we ship to clients.
            </p>
            <p className="text-zinc-400 leading-relaxed mt-4">
              Beyond shipping software, we run programs that bridge classrooms and codebases — turning students into
              production-ready engineers, and turning ideas into shippable software.
            </p>
          </Reveal>
        </div>

        {/* WORK GALLERY — software / engineering / education photos */}
        <div className="mt-28">
          <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Inside the studio</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">Where the <span className="fb-text">code & coffee</span> happen.</h2></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10" data-testid="about-gallery">
            {ABOUT_GALLERY.map((g, i) => (
              <Reveal key={g.src} delay={i * 0.05}>
                <motion.div whileHover={{ y: -6, scale: 1.02 }} className={`relative rounded-3xl overflow-hidden border border-white/10 group ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto" : "aspect-square md:aspect-[4/3]"}`}>
                  <img src={g.src} alt={g.caption} loading="lazy" className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <p className="absolute bottom-3 left-4 right-4 text-sm font-medium text-white">{g.caption}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="mt-28">
          <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">What drives us</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">Values that <span className="fb-text">ship</span>.</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <motion.div whileHover={{ y: -8 }} className="glass rounded-2xl p-7 h-full hover:border-[#FF6B1A]/40 transition-colors relative overflow-hidden group">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${v.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <v.icon className="text-black" size={20} />
                  </div>
                  <h3 className="font-semibold mb-2">{v.title}</h3>
                  <p className="text-sm text-zinc-400">{v.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-28">
          <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#3B82F6] uppercase">Milestones</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">A journey of <span className="azure-text">trust, code & growth</span>.</h2></Reveal>
          <div className="mt-12 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#FF6B1A] via-[#3B82F6] to-transparent" />
            {TIMELINE.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05}>
                <div className={`relative flex gap-6 mb-10 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-gradient-to-br from-[#FF6B1A] to-[#3B82F6] ring-4 ring-[#09090B] mt-2" />
                  <div className="ml-12 md:ml-0 md:w-1/2 md:px-10">
                    <div className="glass rounded-2xl p-6">
                      <span className="text-xs font-bold tracking-[0.25em] text-[#FFAA33] uppercase">{t.year}</span>
                      <h3 className="mt-2 text-xl font-semibold">{t.title}</h3>
                      <p className="text-sm text-zinc-400 mt-2">{t.desc}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Founders */}
        <div className="mt-28">
          <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Leadership</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">Meet the <span className="fb-text">founders</span>.</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {FOUNDERS.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.08}>
                <motion.div whileHover={{ y: -6 }} className="glass rounded-3xl p-8 flex flex-col sm:flex-row gap-6 items-center sm:items-start hover:border-[#FF6B1A]/40 transition-all relative overflow-hidden">
                  <div className="absolute -top-20 -right-20 orb orb-flame w-[260px] h-[260px] opacity-20" />
                  <div className="relative shrink-0">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#FF6B1A] to-[#3B82F6] blur-md opacity-50" />
                    <img src={f.photo} alt={f.name} className="relative w-32 h-32 rounded-2xl object-cover border border-white/15" />
                  </div>
                  <div className="relative">
                    <h3 className="text-2xl font-bold">{f.name}</h3>
                    <p className="fb-text font-semibold text-sm mb-3">{f.role}</p>
                    <p className="text-zinc-400 text-sm leading-relaxed">{f.bio}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
